import { useEffect, useRef, useState } from 'react';
import { Check, Pencil, Trash2 } from 'lucide-react';
import styles from '../HomepageSettings.module.css';

/**
 * One line of the homepage's moving trust strip.
 *
 * Shown as plain text with an edit pen; the pen turns it into an input, Enter or the tick
 * confirms and Escape puts back what it said before. A brand-new (empty) line starts in edit
 * mode so "Add Ticker Item" lands the cursor straight in the box.
 *
 * The parent owns the text and is updated on every keystroke, exactly as before — this only
 * changes how the line is presented, not how it is saved.
 */
export default function MarqueeItemCard({ value, onChange, onRemove }) {
  const text = value ?? '';
  const [editing, setEditing] = useState(text === '');
  const before = useRef(text);
  const inputRef = useRef(null);

  useEffect(() => {
    if (editing) inputRef.current?.focus();
  }, [editing]);

  const startEditing = () => {
    before.current = text;
    setEditing(true);
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      setEditing(false);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onChange(before.current);
      setEditing(false);
    }
  };

  return (
    <div className={`${styles.itemCard} ${styles.marqueeItemCard}`}>
      <div className={styles.marqueeRow}>
        <div className={`${styles.field} ${styles.marqueeField}`}>
          <label>Item Text</label>
          {editing ? (
            <input
              ref={inputRef}
              value={text}
              placeholder="e.g. 24/7 Support"
              onChange={(e) => onChange(e.target.value)}
              onKeyDown={onKeyDown}
            />
          ) : (
            <div className={styles.marqueeText} title={text}>{text}</div>
          )}
        </div>
        {editing ? (
          <button
            type="button"
            className={styles.marqueeIconBtn}
            aria-label="Done editing"
            title="Done"
            onClick={() => setEditing(false)}
          >
            <Check size={14} />
          </button>
        ) : (
          <button
            type="button"
            className={styles.marqueeIconBtn}
            aria-label="Edit ticker item"
            title="Edit"
            onClick={startEditing}
          >
            <Pencil size={14} />
          </button>
        )}
        <button
          type="button"
          className={`${styles.dangerBtn} ${styles.marqueeRemoveBtn}`}
          onClick={onRemove}
        >
          <Trash2 size={14} />
          Remove
        </button>
      </div>
    </div>
  );
}
